import React, { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import AppLayout from '../../../../component/appLayout';
import { changePasswordDetail } from '../../../../services/authServices';
import { PasswordDetailInterface } from '../../../../interfaces/AuthInterface';
import { Group, Details, FormBlock, PersonalText, Header, Container } from '../../styles';
import Button from '../../../../component/button';
import { isEmpty } from '../../../../helpers/common';
import { captureException } from '../../../../services/logService';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import { rootStore } from '../../../../mobx/rootStore';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { BUTTON_TYPE } from '../../../../global/constants';
import Input from '../../../../component/input';

const ChangePassword: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);

  // MobX store
  const { settingStore } = rootStore;
  const { passwordDetail, profileData } = settingStore;
  const { oldPassword, newPassword, confirmPassword } = passwordDetail;

  // Other variables
  const isMobile = useMobileDevice();

  // Updates password input fields in the state
  const onChangePassword = useCallback(
    (propsName: string, value: string) => {
      settingStore.updatePasswordDetail(propsName, value);
    },
    [settingStore]
  );

  // Handles form submission and changes password
  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      try {
        e.preventDefault();
        setLoading(true);
        if (!profileData?.emptyPassword && isEmpty(oldPassword)) {
          rootStore.alertNotificationStore.setErrorMessage('Please enter current password.');
          return;
        } else if (isEmpty(newPassword)) {
          rootStore.alertNotificationStore.setErrorMessage('Please enter new password.');
          return;
        } else if (isEmpty(confirmPassword)) {
          rootStore.alertNotificationStore.setErrorMessage('Please enter confirm password.');
          return;
        } else if (newPassword !== confirmPassword) {
          rootStore.alertNotificationStore.setErrorMessage('Please enter new password and confirm password correctly.');
          return;
        }
        const payload: PasswordDetailInterface = {
          newPassword,
          confirmPassword
        };
        if (!profileData?.emptyPassword) {
          payload.oldPassword = oldPassword;
        }

        const result = await changePasswordDetail(payload);
        if (result) {
          settingStore.clearPasswordDetail();
        }
      } catch (error) {
        captureException(error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [oldPassword, newPassword, confirmPassword, settingStore, profileData?.emptyPassword]
  );

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group as='form' onSubmit={onSubmit} isMiddleContainer={true}>
            {!isMobile ? (
              <Header>
                <PersonalText>Change Password</PersonalText>
              </Header>
            ) : (
              <ResponsiveHeader title='Change Password' />
            )}
            <Details>
              <FormBlock isNewRow={true}>
                {!profileData?.emptyPassword && (
                  <Input
                    label='Current Password'
                    type='password'
                    value={oldPassword}
                    onChange={(e) => onChangePassword('oldPassword', e.target.value)}
                    fieldWrapperWidth={isMobile ? '100%' : '260px'}
                  />
                )}
                <Input
                  label='New Password'
                  type='password'
                  value={newPassword}
                  onChange={(e) => onChangePassword('newPassword', e.target.value)}
                  fieldWrapperWidth={isMobile ? '100%' : '260px'}
                />
                <Input
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => onChangePassword('confirmPassword', e.target.value)}
                  fieldWrapperWidth={isMobile ? '100%' : '260px'}
                />
              </FormBlock>
              <Button
                title='Save'
                variant={BUTTON_TYPE.PRIMARY}
                buttonType='submit'
                isLoading={loading}
                disabled={loading}
              />
            </Details>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(ChangePassword);
